import { inject, Injectable } from '@angular/core';
import type { TimeSeries } from '@clean-code/models';
import { TranslocoService } from '@jsverse/transloco';
import dayjs from 'dayjs';
import PivotGridDataSource from 'devextreme/ui/pivot_grid/data_source';
import { PivotTableService } from './pivot-table.service';

const DOTNET_EPOCH_TICKS = 621355968000000000;
const TICKS_PER_DAY = 864000000000;
const MS_PER_DAY = 86400000;

interface IdentifierKey {
  parserType: number;
  /** Days since 1970-01-01 */
  deliveryDay: number;
  ricName: string;
}

/**
 * Parses the key built in ContollingQueryHC.cs:
 * `${dateIsOlder}_${parserType}_${Timestamp.Date.Ticks}_${identifier}`
 */
function parseIdentifierKey(key: string | undefined): IdentifierKey | null {
  const [, parserType, ticks, ...ricName] = key?.split('_') ?? [];
  if (ricName.length === 0) return null;

  return {
    parserType: Number.parseInt(parserType),
    // .NET ticks exceed Number.MAX_SAFE_INTEGER, but whole days survive the rounding
    deliveryDay: Math.round((Number(ticks) - DOTNET_EPOCH_TICKS) / TICKS_PER_DAY),
    ricName: ricName.join('_'), // ricNames can contain underscores
  };
}

@Injectable({
  providedIn: 'root',
})
export class PivotTableConfigService {
  translateService = inject(TranslocoService);
  private pivotTableService = inject(PivotTableService);

  dataSourceConfig(data: TimeSeries[]): PivotGridDataSource {
    const dataSourceConfig = new PivotGridDataSource({
      store: data,
      fields: [
        {
          caption: this.translateService.translate('raw-data.fields.DATE'),
          dataField: 'tradeDate',
          area: 'row',
          dataType: 'date',
          groupInterval: 'day',
          sortOrder: 'desc',
          selector: this.dateSelector,
          width: 100,
          isMeasure: false,
          customizeText: (e) => {
            if (e.value !== undefined && e.value !== null) {
              e.valueText = dayjs(e.value).format('DD.MM.YYYY');
            } else {
              e.valueText = '';
            }
            return e.valueText;
          },
        },
        {
          caption: this.translateService.translate(
            'raw-data.filters.IDENTIFIER',
          ),
          dataField: 'identifier',
          area: 'column',
          sortingMethod: this.identifierSortingMethod(data),
          dataType: 'string',
          selector: this.symbolSelector,
          isMeasure: false,
        },

        {
          caption: this.translateService.translate(
            'raw-data.fields.SETTLEMENT_PRICE',
          ),
          dataField: 'settlementPrice',
          summaryType: 'sum',
          area: 'data',
          isMeasure: true,
          format: 'decimal',
          customizeText: (e) => {
            if (e.value !== undefined && e.value !== null) {
              e.valueText = this.formatNumber(e.value);
            } else {
              e.valueText = '';
            }
            return e.valueText;
          },
        },
        {
          caption: this.translateService.translate(
            'raw-data.fields.OPEN_PRICE',
          ),
          dataField: 'openPrice',
          summaryType: 'sum',
          area: null,
          isMeasure: true,
          format: 'decimal',
          customizeText: (e) => {
            if (e.value !== undefined && e.value !== null) {
              e.valueText = this.formatNumber(e.value);
            } else {
              e.valueText = '';
            }
            return e.valueText;
          },
        },
        {
          caption: this.translateService.translate(
            'raw-data.fields.HIGH_PRICE',
          ),
          dataField: 'highPrice',
          summaryType: 'sum',
          isMeasure: true,
          format: 'decimal',
          customizeText: (e) => {
            if (e.value !== undefined && e.value !== null) {
              e.valueText = this.formatNumber(e.value);
            } else {
              e.valueText = '';
            }
            return e.valueText;
          },
        },
        {
          caption: this.translateService.translate('raw-data.fields.LOW_PRICE'),
          dataField: 'lowPrice',
          summaryType: 'sum',
          isMeasure: true,
          format: 'decimal',
          customizeText: (e) => {
            if (e.value !== undefined && e.value !== null) {
              e.valueText = this.formatNumber(e.value);
            } else {
              e.valueText = '';
            }
            return e.valueText;
          },
        },
        {
          caption: this.translateService.translate('raw-data.fields.VOLUME'),
          dataField: 'volume',
          summaryType: 'sum',
          isMeasure: true,
          format: 'decimal',
          customizeText: (e) => {
            if (e.value !== undefined && e.value !== null) {
              e.valueText = this.formatNumber(e.value);
            } else {
              e.valueText = '';
            }
            return e.valueText;
          },
        },
        {
          dataField: 'id',
          visible: false,
        },
        {
          dataField: 'isIntraday',
          visible: false,
        },
        {
          dataField: 'unit',
          visible: false,
        },
        {
          dataField: 'timestamp',
          visible: false,
        },
        {
          dataField: 'tradeDate',
          visible: false,
        },
      ],
      onChanged: () => {
        this.pivotTableService.setPivotTableData(
          dataSourceConfig.getAreaFields('data', false),
        );
      },
    });
    return dataSourceConfig;
  }

  /**
   * Active contracts first (by parserType, delivery day, ricName), expired contracts at the end.
   */
  private identifierSortingMethod(data: TimeSeries[]) {
    // The column header value is the symbolSelector text, not the identifier
    const keysByColumn = new Map<string, IdentifierKey>();
    for (const item of data) {
      const key = parseIdentifierKey(item.key);
      if (!key) continue;

      // Spot identifiers repeat on every trade date, use the latest delivery day
      const column = this.symbolSelector(item);
      const current = keysByColumn.get(column);
      if (!current || key.deliveryDay > current.deliveryDay) {
        keysByColumn.set(column, key);
      }
    }

    // The dateIsOlder flag of the key compares the exact hour with DateTime.Now, which splits
    // the hourly spot products of the current day, so expiry is decided on the delivery day.
    const now = new Date();
    const today =
      Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / MS_PER_DAY;

    return (
      a: { value?: string | number },
      b: { value?: string | number },
    ) => {
      const keyA = keysByColumn.get(a.value?.toString() ?? '');
      const keyB = keysByColumn.get(b.value?.toString() ?? '');

      if (!keyA || !keyB) return 0;

      const isExpiredA = keyA.deliveryDay < today;
      const isExpiredB = keyB.deliveryDay < today;

      if (isExpiredA !== isExpiredB) {
        return isExpiredA ? 1 : -1;
      }

      if (keyA.parserType !== keyB.parserType) {
        return keyA.parserType - keyB.parserType;
      }

      if (keyA.deliveryDay !== keyB.deliveryDay) {
        return keyA.deliveryDay - keyB.deliveryDay;
      }

      return keyA.ricName.localeCompare(keyB.ricName);
    };
  }

  symbolSelector(data: TimeSeries) {
    if (data.name) {
      return `${data.name}, ${data.identifier}, ${data.unit}`;
    }
    return `${data.identifier}, ${data.unit}`;
  }
  dateSelector(data: TimeSeries) {
    return dayjs(data.tradeDate).format('YYYY-MM-DD');
  }

  formatNumber(value: number | string | Date) {
    let numericValue: number;
    if (typeof value === 'string') {
      numericValue = Number(value);
    } else if (value instanceof Date) {
      numericValue = value.valueOf();
    } else {
      numericValue = value;
    }
    return Number(numericValue).toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
}
